import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
} from '@mui/material';
import { AddCircleOutline } from '@mui/icons-material';
import { useGetCentersQuery } from '../../../../services/DimensionService/dimensionApi';
import { useSeedComplianceItemsMutation } from '../../../../services/ComplianceService/complianceMutations';

/**
 * SeedComplianceItemsButton - lets a compliance coordinator populate every standard life-safety/EOC
 * requirement (data-model.md's 14-item catalog) for one facility in a single action. Idempotent —
 * safe to run again for a facility that already has some or all requirements (US1, FR-005).
 */
export const SeedComplianceItemsButton: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [center, setCenter] = useState('');
  const [resultMessage, setResultMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const { data: centers = [] } = useGetCentersQuery();
  const [seedComplianceItems, { isLoading }] = useSeedComplianceItemsMutation();

  const handleClose = () => {
    setOpen(false);
    setCenter('');
    setResultMessage('');
    setErrorMessage('');
  };

  const handleSeed = async () => {
    if (!center) return;

    try {
      const created = await seedComplianceItems({ centerId: center }).unwrap();
      setErrorMessage('');
      setResultMessage(`Seeded ${created.length} new requirement${created.length !== 1 ? 's' : ''}.`);
    } catch (error) {
      console.error('Failed to seed standard compliance requirements:', error);
      setResultMessage('');
      setErrorMessage('Failed to seed standard requirements for this facility. Please try again.');
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<AddCircleOutline />}
        onClick={() => setOpen(true)}
        sx={{ textTransform: 'none' }}
      >
        Seed Standard Requirements
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle>Seed Standard Requirements</DialogTitle>
        <DialogContent>
          {resultMessage && <Alert severity="success" sx={{ mb: 2 }}>{resultMessage}</Alert>}
          {errorMessage && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage}</Alert>}
          <TextField
            label="Facility"
            size="small"
            select
            required
            fullWidth
            value={center}
            onChange={(e) => setCenter(e.target.value)}
            sx={{ mt: 1 }}
          >
            <MenuItem value="">
              <em>Select Facility</em>
            </MenuItem>
            {centers.map((c) => (
              <MenuItem key={c.value} value={c.value}>
                {c.name}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={handleClose} disabled={isLoading}>
            Close
          </Button>
          <Button
            variant="contained"
            onClick={handleSeed}
            disabled={isLoading || !center}
            sx={{ backgroundColor: '#162346', '&:hover': { backgroundColor: '#1a2a52' } }}
          >
            {isLoading ? 'Seeding...' : 'Seed'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SeedComplianceItemsButton;
